import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    ArrowRight, ChevronLeft, ChevronRight, Fingerprint, CalendarDays, CalendarOff, ListChecks, Router, Loader, Plus, Trash2, Copy, RefreshCw, X,
} from "lucide-react";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import useAttendanceSettings from "./useAttendanceSettings";
import {
    PageHeader, HeaderAction, ListLoader, ListEmpty, ListCard, Panel, thClass, RowActions
} from "../../stock/StockUI";
import {inputClass as formInputClass} from "../../ClientsTable/clientFormUI";
import {
    SOURCE_LABELS,
    VERIFY_MODE_LABELS,
    apiErrorMessage,
    createAttendanceDevice,
    createManualPunch,
    deleteAttendanceDevice,
    deletePunch,
    fetchAttendanceDevices,
    fetchAttendancePunches,
    fetchAttendanceSummary,
    formatDayLabel,
    presetRange,
    regenerateDeviceToken,
    shiftPeriod,
    toDateKey,
    updateAttendanceDevice,
} from "./attendanceApi";
import AppleSelect from "../Common/AppleSelect";
import Button from '../Common/Button';
import DateInput from "../Common/DateInput";
import PresenceTab from "./PresenceTab";

const TABS = [
    {key: "presence", label: "Présence", Icon: CalendarDays},
    {key: "punches", label: "Pointages", Icon: ListChecks},
    {key: "devices", label: "Pointeuses", Icon: Router},
];

// Catalogue form field, without the full width (some sit inline)
const inputClass = `${formInputClass(false).replace("w-full ", "")} disabled:opacity-60`;

const cardClass = "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg";

// Table cell paddings of the catalogue lists
const tdClass = "px-6 py-4 text-sm";

const Badge = ({className, children}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${className}`}>{children}</span>
);

const Spinner = () => <ListLoader label="Chargement des pointages..."/>;

// ─── Filtres période ─────────────────────────────────────────────────────────

const PRESETS = [
    {key: "today", label: "Aujourd'hui"},
    {key: "yesterday", label: "Hier"},
    {key: "week", label: "Cette semaine"},
    {key: "month", label: "Ce mois"},
    {key: "lastMonth", label: "Mois dernier"},
];

const dateFieldClass = "w-full h-9 px-1.5 sm:px-2 text-xs sm:text-sm font-medium bg-transparent text-gray-800 dark:text-gray-100 rounded-lg hover:bg-white dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors";
const arrowClass = "flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30 disabled:pointer-events-none transition-colors";

const PeriodBar = ({period, setPeriod, employees}) => {
    const set = (changes) => setPeriod((prev) => ({...prev, ...changes}));
    const today = toDateKey(new Date());
    // A Monday is both "today" and "this week": only the most precise one lights up
    const activePreset = PRESETS.find(({key}) => {
        const range = presetRange(key);
        return range.from === period.from && range.to === period.to;
    })?.key;

    // Wraps on the space really left by the sidebar, not on the screen width
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-full overflow-x-auto no-scrollbar">
                <div role="group" aria-label="Période" className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
                    {PRESETS.map(({key, label}) => {
                        const selected = key === activePreset;
                        return (
                            <button
                                key={key}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => set(presetRange(key))}
                                className={`h-9 px-3.5 rounded-lg whitespace-nowrap text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all ${
                                    selected
                                        ? "bg-white dark:bg-gray-600 text-primary-dark dark:text-white shadow-sm"
                                        : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 w-full sm:w-auto min-w-0">
                <div className="flex items-center p-1 rounded-xl bg-gray-100 dark:bg-gray-800 min-w-0">
                    <button type="button" aria-label="Période précédente" title="Période précédente" className={arrowClass}
                            onClick={() => set(shiftPeriod(period, -1, today))}>
                        <ChevronLeft className="w-4 h-4"/>
                    </button>
                    <div className="flex-1 sm:flex-none sm:w-40 min-w-0">
                        <DateInput aria-label="Du" title="Du" value={period.from} max={today} clearable={false} className={dateFieldClass}
                                   onChange={(e) => e.target.value && set({from: e.target.value, to: e.target.value > period.to ? e.target.value : period.to})}/>
                    </div>
                    <ArrowRight aria-hidden="true" className="hidden sm:block w-4 h-4 flex-shrink-0 mx-0.5 text-gray-400"/>
                    <div className="flex-1 sm:flex-none sm:w-40 min-w-0">
                        <DateInput aria-label="Au" title="Au" value={period.to} min={period.from} max={today} clearable={false} className={dateFieldClass}
                                   onChange={(e) => e.target.value && set({to: e.target.value})}/>
                    </div>
                    <button type="button" aria-label="Période suivante" title="Période suivante" className={arrowClass}
                            disabled={period.to >= today}
                            onClick={() => set(shiftPeriod(period, 1, today))}>
                        <ChevronRight className="w-4 h-4"/>
                    </button>
                </div>
                <AppleSelect size="sm" aria-label="Employé" value={period.employeeId} className="w-full sm:w-56"
                             onChange={(e) => set({employeeId: e.target.value})}
                             options={[{value: "", label: "Tous les employés"}, ...employees.map((e) => ({value: e.id, label: e.name}))]}/>
            </div>
        </div>
    );
};

// ─── Pointages bruts + corrections ───────────────────────────────────────────

const ManualPunchModal = ({employees, defaultDate, onClose}) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({employeeId: "", date: defaultDate, time: "", note: ""});
    const mutation = useMutation({
        mutationFn: () => createManualPunch({employeeId: form.employeeId, punchedAt: `${form.date} ${form.time}`, note: form.note}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["attendance"]});
            toast.success("Pointage ajouté");
            onClose();
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'ajouter le pointage")),
    });
    const set = (field) => (e) => setForm((prev) => ({...prev, [field]: e.target.value}));
    const valid = form.employeeId && form.date && form.time;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50">
            <form
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-lg w-full sm:max-w-[500px] sm:mx-4 overflow-hidden"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (valid) mutation.mutate();
                }}
            >
                <div className="relative p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-center text-gray-800 dark:text-white">Ajouter un pointage</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer"
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X className="w-4 h-4"/>
                    </button>
                </div>
                <div className="px-6 py-4 space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pour corriger un oubli. Le pointage est marqué « Saisie manuelle ».</p>
                    <AppleSelect className={`${inputClass} w-full`} value={form.employeeId} onChange={set("employeeId")}>
                        <option value="">Choisir un employé</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </AppleSelect>
                    <div className="flex gap-3">
                        <DateInput className={`${inputClass} flex-1 min-w-0`} value={form.date} onChange={set("date")}/>
                        <input type="time" className={`${inputClass} flex-1 min-w-0`} value={form.time} onChange={set("time")}/>
                    </div>
                    <input type="text" className={`${inputClass} w-full`} placeholder="Motif (facultatif)" maxLength={255}
                           value={form.note} onChange={set("note")}/>
                </div>
                <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={onClose}
                            className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium">
                        Annuler
                    </button>
                    <button type="submit" disabled={!valid || mutation.isPending}
                            className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {mutation.isPending ? "Enregistrement…" : "Ajouter"}
                    </button>
                </div>
            </form>
        </div>
    );
};

// addRequestKey: bumped by the page header's « Nouveau » to open the add modal
const PunchesTab = ({period, employees, canManage, addRequestKey}) => {
    const queryClient = useQueryClient();
    const [showAdd, setShowAdd] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    useEffect(() => {
        if (addRequestKey > 0) setShowAdd(true);
    }, [addRequestKey]);

    const {data: punches, isLoading} = useQuery({
        queryKey: ["attendance", "punches", period],
        queryFn: () => fetchAttendancePunches(period),
        placeholderData: keepPreviousData,
    });

    const deleteMutation = useMutation({
        mutationFn: deletePunch,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["attendance"]});
            toast.success("Pointage supprimé");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible de supprimer le pointage")),
    });

    return (
        <div className="max-w-full">
            {isLoading || !punches ? <Spinner/> : (
                <Panel title={`${punches.length} pointage(s)`}>
                    {punches.length === 0 ? (
                        <ListEmpty icon={ListChecks} title="Aucun pointage" text="Aucun pointage sur cette période."/>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Date</th>
                                    <th className={thClass}>Heure</th>
                                    <th className={thClass}>Employé</th>
                                    <th className={thClass}>Mode</th>
                                    <th className={thClass}>Source</th>
                                    {canManage && <th className={`${thClass} text-right`}>Actions</th>}
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {punches.map((p) => (
                                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className={`${tdClass} whitespace-nowrap text-gray-500 dark:text-gray-400`}>{formatDayLabel(p.punched_at.slice(0, 10))}</td>
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>{p.time}</td>
                                        <td className={`${tdClass} ${p.employee_id ? "text-gray-900 dark:text-gray-100" : "text-amber-600"}`}>
                                            {p.employee_name || `Badge n° ${p.badge_number} (non attribué)`}
                                        </td>
                                        <td className={`${tdClass} text-gray-700 dark:text-gray-300`}>{VERIFY_MODE_LABELS[p.verify_mode] || p.verify_mode}</td>
                                        <td className={`${tdClass} text-gray-500 dark:text-gray-400`}>
                                            {SOURCE_LABELS[p.source] || p.source}
                                            {p.device_name ? ` · ${p.device_name}` : ""}
                                            {p.note ? <span className="block text-xs text-gray-400">{p.note}</span> : null}
                                        </td>
                                        {canManage && (
                                            <td className={`${tdClass} text-right`}>
                                                <RowActions actions={[
                                                    {label: "Supprimer", icon: Trash2, tone: "red", danger: true, onClick: () => setToDelete(p)},
                                                ]}/>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Panel>
            )}

            {showAdd && <ManualPunchModal employees={employees.filter((e) => e.active)} defaultDate={period.to} onClose={() => setShowAdd(false)}/>}
            {toDelete && (
                <ConfirmationDialog
                    title="Supprimer ce pointage ?"
                    message={`${toDelete.employee_name || `Badge n° ${toDelete.badge_number}`} — ${toDelete.time}. Les entrées/sorties de la journée seront recalculées.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    danger
                    onCancel={() => setToDelete(null)}
                    onConfirm={() => {
                        deleteMutation.mutate(toDelete.id);
                        setToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

// ─── Pointeuses ──────────────────────────────────────────────────────────────

const DeviceCard = ({device, canManage}) => {
    const queryClient = useQueryClient();
    const [confirm, setConfirm] = useState(null); // "regenerate" | "delete"
    const refresh = () => queryClient.invalidateQueries({queryKey: ["attendance", "devices"]});
    const onError = (fallback) => (error) => toast.error(apiErrorMessage(error, fallback));

    const toggleMutation = useMutation({
        mutationFn: () => updateAttendanceDevice({id: device.id, active: !device.active}),
        onSuccess: refresh,
        onError: onError("Impossible de modifier la pointeuse"),
    });
    const regenerateMutation = useMutation({
        mutationFn: () => regenerateDeviceToken(device.id),
        onSuccess: () => {
            refresh();
            toast.success("Nouvelle adresse générée : reconfigurez la pointeuse");
        },
        onError: onError("Impossible de régénérer l'adresse"),
    });
    const deleteMutation = useMutation({
        mutationFn: () => deleteAttendanceDevice(device.id),
        onSuccess: refresh,
        onError: onError("Impossible de supprimer la pointeuse"),
    });

    let url = null;
    try {
        url = new URL(device.push_url);
    } catch {
        // malformed URL from the API: only the raw string is shown
    }
    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copié");
        } catch {
            toast.error("Copie impossible");
        }
    };

    return (
        <div className={`${cardClass} p-4 sm:p-6 space-y-4 hover:shadow-md transition-all`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{device.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {device.serial_number || "N° de série inconnu"} · {device.last_seen_at
                        ? `Dernier contact : ${new Date(device.last_seen_at).toLocaleString("fr-FR")}`
                        : "Jamais connectée"}
                    </p>
                </div>
                <Badge className={device.active
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}>
                    {device.active ? "Active" : "Désactivée"}
                </Badge>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adresse d'envoi des pointages :</p>
                <div className="flex gap-2">
                    <input readOnly value={device.push_url} className={`${inputClass} flex-1 min-w-0 font-mono !text-xs`}
                           onFocus={(e) => e.target.select()}/>
                    <button type="button" onClick={() => copy(device.push_url)} title="Copier"
                            className="px-3 rounded-lg border border-primary text-primary hover:bg-primary hover:text-white transition-colors">
                        <Copy className="w-4 h-4"/>
                    </button>
                </div>
            </div>

            {url && (
                <details className="text-sm text-gray-600 dark:text-gray-300">
                    <summary className="cursor-pointer text-primary">Configurer la pointeuse Hikvision</summary>
                    <ol className="list-decimal ml-5 mt-2 space-y-1 text-xs">
                        <li>Ouvrir l'interface web de la pointeuse (son adresse IP) et se connecter en admin.</li>
                        <li>Configuration → Réseau → Paramètres avancés → <b>HTTP Listening</b> (Écoute HTTP).</li>
                        <li>
                            Adresse : <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{url.hostname}</code>,
                            port : <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{url.port || (url.protocol === "https:" ? "443" : "80")}</code>,
                            protocole : <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{url.protocol === "https:" ? "HTTPS" : "HTTP"}</code>
                        </li>
                        <li>
                            URL : <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded break-all">{url.pathname}</code>
                            <button type="button" onClick={() => copy(url.pathname)} className="ml-1 text-primary hover:underline">copier</button>
                        </li>
                        <li>Vérifier l'heure : fuseau <b>UTC+01:00</b> et synchronisation <b>NTP</b>, sinon les heures seront décalées.</li>
                        <li>Pointer une fois : « Dernier contact » doit se mettre à jour ici.</li>
                    </ol>
                </details>
            )}

            {canManage && (
                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={() => toggleMutation.mutate()} disabled={toggleMutation.isPending}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        {device.active ? "Désactiver" : "Activer"}
                    </button>
                    <button type="button" onClick={() => setConfirm("regenerate")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <RefreshCw className="w-4 h-4"/> Nouvelle adresse
                    </button>
                    <button type="button" onClick={() => setConfirm("delete")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <Trash2 className="w-4 h-4"/> Supprimer
                    </button>
                </div>
            )}

            {confirm && (
                <ConfirmationDialog
                    title={confirm === "delete" ? "Supprimer cette pointeuse ?" : "Générer une nouvelle adresse ?"}
                    message={confirm === "delete"
                        ? "Les pointages déjà reçus sont conservés, mais la pointeuse ne pourra plus en envoyer."
                        : "L'adresse actuelle cessera de fonctionner immédiatement : il faudra saisir la nouvelle sur la pointeuse."}
                    confirmLabel={confirm === "delete" ? "Supprimer" : "Générer"}
                    cancelLabel="Annuler"
                    danger
                    onCancel={() => setConfirm(null)}
                    onConfirm={() => {
                        (confirm === "delete" ? deleteMutation : regenerateMutation).mutate();
                        setConfirm(null);
                    }}
                />
            )}
        </div>
    );
};

const DevicesTab = ({canManage}) => {
    const queryClient = useQueryClient();
    const [name, setName] = useState("");
    const {data: devices, isLoading} = useQuery({
        queryKey: ["attendance", "devices"],
        queryFn: fetchAttendanceDevices,
    });
    const createMutation = useMutation({
        mutationFn: () => createAttendanceDevice(name.trim()),
        onSuccess: () => {
            setName("");
            queryClient.invalidateQueries({queryKey: ["attendance", "devices"]});
            toast.success("Pointeuse ajoutée");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'ajouter la pointeuse")),
    });

    if (isLoading || !devices) return <ListLoader label="Chargement des pointeuses..."/>;

    // Centred like the catalogue forms
    return (
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 w-full">
            {canManage && (
                <form
                    className="flex flex-col sm:flex-row gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (name.trim()) createMutation.mutate();
                    }}
                >
                    <input className={`${inputClass} flex-1 min-w-0`} placeholder="Nom de la pointeuse (ex. Entrée personnel)"
                           value={name} maxLength={100} onChange={(e) => setName(e.target.value)}/>
                    <button type="submit" disabled={!name.trim() || createMutation.isPending}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 sm:py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors whitespace-nowrap disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-primary">
                        {createMutation.isPending ? <Loader className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>} Ajouter
                    </button>
                </form>
            )}
            {devices.length === 0 ? (
                <ListCard>
                    <ListEmpty icon={Router} title="Aucune pointeuse" text="Ajoutez une pointeuse pour recevoir les pointages."/>
                </ListCard>
            ) : (
                devices.map((d) => <DeviceCard key={d.id} device={d} canManage={canManage}/>)
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Sur la pointeuse, le « N° employé » de chaque personne doit être son n° de badge (fiche employé).
            </p>
        </div>
    );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const AttendancePage = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("attendance");
    const canDeclareRest = canUpdate("calendar") || canUpdate("settings");
    const [activeTab, setActiveTab] = useState("presence");
    const [period, setPeriod] = useState(() => ({...presetRange("today"), employeeId: ""}));
    const [addPunchKey, setAddPunchKey] = useState(0);
    const [declareRestKey, setDeclareRestKey] = useState(0);
    const {enabled: moduleEnabled, isLoading: settingsLoading} = useAttendanceSettings();

    // The work-day start comes from the account (Réglages > Pointage): the API applies it.
    // Always the whole team, filtered in Présence: absences are counted from the first
    // punch of anyone in the period, and the employee filter must not change that.
    const summaryPeriod = {from: period.from, to: period.to, employeeId: ""};
    const {data: summary, isLoading} = useQuery({
        queryKey: ["attendance", "summary", summaryPeriod],
        queryFn: () => fetchAttendanceSummary(summaryPeriod),
        placeholderData: keepPreviousData,
        enabled: moduleEnabled && activeTab !== "devices",
        refetchInterval: 60 * 1000,
    });
    const employees = summary?.employees || [];

    if (settingsLoading) return <ListLoader/>;
    if (!moduleEnabled) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900">
                <PageHeader title="Pointage"/>
                <ListEmpty
                    icon={Fingerprint}
                    title="Pointage désactivé"
                    text="Le module Pointage n'est pas activé pour ce point de vente."
                    action={
                        <Button
                            as={Link}
                            to="/settings"
                            variant="tinted"
                            className="mt-4"
                            onClick={() => {
                                try {
                                    localStorage.setItem("tab_settings", "pointage"); // read by Settings' usePersistedTab
                                } catch {
                                    // the user just lands on the default settings tab
                                }
                            }}
                        >
                            Réglages → Équipe → Pointage
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <PageHeader
                title="Pointage"
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                actions={
                    activeTab === "punches" && canManage ? (
                        <HeaderAction icon={Plus} label="Nouveau" onClick={() => setAddPunchKey((k) => k + 1)}/>
                    ) : activeTab === "presence" && canDeclareRest ? (
                        <HeaderAction icon={CalendarOff} label="Congé / repos" onClick={() => setDeclareRestKey((k) => k + 1)}/>
                    ) : null
                }
            />

            {activeTab !== "devices" && <PeriodBar period={period} setPeriod={setPeriod} employees={employees}/>}
            {activeTab === "presence" && <PresenceTab period={period} summary={summary} isLoading={isLoading} declareKey={declareRestKey}/>}
            {activeTab === "punches" && (
                <PunchesTab period={period} employees={employees} canManage={canManage} addRequestKey={addPunchKey}/>
            )}
            {activeTab === "devices" && <DevicesTab canManage={canManage}/>}
        </div>
    );
};

export default AttendancePage;
